<script setup lang="ts">
  import { useQuery } from '@tanstack/vue-query'
  import { onKeyStroke, useScrollLock } from '@vueuse/core'
  import { onServerPrefetch, ref, watch } from 'vue'
  import BannerAd from '~renderer/components/ads/BannerAd.vue'
  import Carousel from '~renderer/components/Carousel.vue'
  import CommunitySupport from '~renderer/components/CommunitySupport.vue'
  import Footer from '~renderer/components/Footer.vue'
  import Header from '~renderer/components/Header.vue'
  import HostWizard from '~renderer/components/HostWizard.vue'
  import IslandActions from '~renderer/components/IslandActions.vue'
  import IslandsList from '~renderer/components/IslandsList.vue'
  import JoinIsland from '~renderer/components/JoinIsland.vue'
  import { useAuth } from '~renderer/composables/useAuth'
  import { guard } from '~renderer/util/telefunc'
  import { onGetOpenIslands } from '~rpc/procedures/island/onGetOpenIslands.telefunc'

  const { requiresUsernameReset } = useAuth()
  const hostWizardOpen = ref(false)
  const joinIslandOpen = ref(false)
  const isScrollLocked = useScrollLock(typeof window !== 'undefined' ? document.body : null)

  onKeyStroke('Escape', () => {
    hostWizardOpen.value = false
    joinIslandOpen.value = false
  })

  const {
    data: islands,
    isLoading,
    refetch,
    suspense,
  } = useQuery({
    queryKey: ['islands'],
    queryFn: () => guard(onGetOpenIslands()),
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  watch([hostWizardOpen, joinIslandOpen], ([hostOpen, joinOpen]) => {
    isScrollLocked.value = hostOpen || joinOpen
  })

  onServerPrefetch(suspense)
</script>

<template>
  <div
    v-if="hostWizardOpen || joinIslandOpen"
    class="pointer-events-auto fixed inset-0 z-30 flex h-screen w-screen items-center justify-center bg-purple-500/80"
    @click.self="hostWizardOpen = joinIslandOpen = false"
  >
    <HostWizard v-if="hostWizardOpen" @cancel="hostWizardOpen = false" />
    <JoinIsland v-if="joinIslandOpen" />
  </div>
  <div
    class="relative min-h-screen bg-purple-500 font-sans text-white"
    v-if="!requiresUsernameReset"
    :inert="hostWizardOpen || joinIslandOpen"
  >
    <Header />

    <!-- Hero Section with Carousel -->
    <Carousel />

    <BannerAd class="mt-16" />
    <!-- Spacer/Dark Area -->
    <div class="h-16 w-full"></div>

    <!-- Main Content Container -->
    <main class="relative mx-auto max-w-6xl space-y-8 px-4 pb-20">
      <CommunitySupport />
      <IslandActions @hostIsland="hostWizardOpen = true" @joinIsland="joinIslandOpen = true" />
    </main>

    <IslandsList v-if="islands" :islands="islands" :isLoading="isLoading" @refresh="refetch" />
    <Footer />
  </div>
</template>
